/*
 * Copyright (c) 2025. Bluemate B.V.
 */
import './style/bluemateStatCard.scss';

import React, {MouseEventHandler, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {Icon} from '@iconify/react';
import trendingUp from '@iconify/icons-feather/trending-up';
import trendingDown from '@iconify/icons-feather/trending-down';
import minus from '@iconify/icons-feather/minus';
import classNames from 'classnames';
import {formatCurrency, parseThousandSeparator, truncateNumber} from '../../../util/utils';
import {StatCardData} from '../../../util/interfaces';
import {StatTrend} from '../../../util/types';
import {BluemateTooltip} from '../index';
import {AwesomeIcon} from '@dvrd/dvr-controls';
import {StatCardDataType} from '../../../util/enums.ts';

interface Props {
    onSwapData: MouseEventHandler;
    onChangeDataType: (dataType: StatCardDataType) => MouseEventHandler;
    data: StatCardData;
    hasMultipleData: boolean;
    visibleDataType: StatCardDataType | 'original';
    className?: string;
    tooltip?: string;
    /** Names the window the card compares against, e.g. "Dag ervoor". Defaults
     * to a plain "Vorige" for views that have no period notion. */
    comparisonLabel?: string;
    /** Drops the trend icon, change badge and previous-value footer — for
     * periods that have no window before them ("Alles"). */
    hideComparison?: boolean;
}

interface TrendStyle {
    icon: typeof trendingUp;
    arrow: string;
    className: string;
}

const trendConfig: Record<StatTrend, TrendStyle> = {
    up: {icon: trendingUp, arrow: '\u2191', className: 'positive'},
    down: {icon: trendingDown, arrow: '\u2193', className: 'negative'},
    neutral: {icon: minus, arrow: '\u2014', className: 'neutral'}
};

const invertedTrendConfig: Record<StatTrend, TrendStyle> = {
    up: {...trendConfig.up, className: 'negative'},
    down: {...trendConfig.down, className: 'positive'},
    neutral: {...trendConfig.neutral, className: 'neutral'}
};

export default function BluemateStatCard(props: Props) {
    const {t} = useTranslation();
    const {data, className, tooltip, comparisonLabel, hideComparison, visibleDataType, hasMultipleData} = props;

    const {
        label,
        value,
        prevValue,
        valueType = 'generic',
        invertTrend,
        useThousandSeparator,
        zeroAsDash = false,
        info,
        noToggles,
        alternates,
        truncateThreshold,
        hidePercentageToggle,
        swapValuePercentage
    } = data;
    const [diff, percentage] = useMemo(() => {
        let percentage: number;
        const diff = value === null ? (prevValue ?? 0) : value - (prevValue ?? 0);
        if (value === null || value === prevValue) percentage = 0;
        else if (value === 0) percentage = -100;
        else if (prevValue === 0 || prevValue === null) percentage = 100;
        else percentage = ((value - prevValue) / prevValue) * 100;
        return [diff, Math.abs(percentage).toFixed(1)];
    }, [value, prevValue]);
    const effectiveTrend = diff === 0 ? 'neutral' : diff < 0 ? 'down' : 'up';
    const config = invertTrend ? invertedTrendConfig[effectiveTrend] : trendConfig[effectiveTrend];

    function renderValue(value: number | null): string | number {
        if (value === null || (value === 0 && zeroAsDash)) return '\u2014';
        if (valueType === StatCardDataType.PERCENTAGE) return `${value.toFixed(1).replace('.', ',')}%`;
        let stringValue: string = value.toString();
        let suffix: string | null = null;
        if (truncateThreshold && truncateThreshold >= 1000 && value > truncateThreshold) {
            [value, suffix] = truncateNumber(value);
            if (suffix) stringValue = value.toFixed(1);
        }
        if (valueType === StatCardDataType.CURRENCY) stringValue = formatCurrency(value);
        else if (useThousandSeparator) stringValue = parseThousandSeparator(stringValue);
        return `${stringValue}${suffix ? suffix : ''}`;
    }

    function renderPercentage() {
        if (Number(percentage) === 0) return `${percentage}%`;
        return `${config.arrow} ${percentage}%`;
    }

    function getToggleTitle(dataType: StatCardDataType | 'swap') {
        if (dataType === StatCardDataType.PERCENTAGE)
            return t('controls.statCard.percentageView', 'Percentage weergave');
        return t('controls.statCard.toggleView', 'Weergave wisselen');
    }

    function getToggleIcon(dataType: StatCardDataType | 'swap') {
        if (dataType === StatCardDataType.PERCENTAGE) return 'lucide:percent';
        return 'lucide:arrow-left-right';
    }

    function renderAlternateToggle(alternate: StatCardData, index: number) {
        const dataType = alternate.valueType ?? StatCardDataType.GENERIC;
        return renderToggle(dataType, index);
    }

    function renderSwapToggle() {
        if (!hasMultipleData) return null;
        return renderToggle('swap');
    }

    function renderToggle(dataType: StatCardDataType | 'swap', index?: number) {
        const isActive = visibleDataType === dataType;
        const onClick = dataType === 'swap' ? props.onSwapData : props.onChangeDataType(dataType);
        return (
            <button
                key={index}
                className={classNames('toggle-btn', isActive && 'active')}
                onClick={onClick}
                title={getToggleTitle(dataType)}
            >
                <Icon icon={getToggleIcon(dataType)} className='toggle-icon' />
            </button>
        );
    }

    function renderToggles() {
        if ((!alternates?.length && prevValue === null) || noToggles) return null;
        const hasPercentageAlternate = alternates?.some(
            alternate => alternate.valueType === StatCardDataType.PERCENTAGE
        );
        return (
            <div className='toggle-actions'>
                {renderSwapToggle()}
                {alternates?.map(renderAlternateToggle)}
                {!hasPercentageAlternate && !hidePercentageToggle && renderToggle(StatCardDataType.PERCENTAGE)}
            </div>
        );
    }

    function renderInfo() {
        if (!info) return null;
        if (React.isValidElement(info)) return info;
        return (
            <BluemateTooltip text={info}>
                <AwesomeIcon name='info-circle' className='info-icon' />
            </BluemateTooltip>
        );
    }

    function renderLabel() {
        if (tooltip)
            return (
                <BluemateTooltip text={tooltip}>
                    <span className='stat-card-label dashed' title={label}>
                        {label}
                    </span>
                </BluemateTooltip>
            );
        return (
            <span className='stat-card-label' title={label}>
                {label}
            </span>
        );
    }

    return (
        <div className={classNames('bluemate-stat-card', className)}>
            <div className='stat-card-header'>
                {renderLabel()}
                <div className='stat-card-actions'>
                    {renderToggles()}
                    {!hideComparison && (
                        <div className={classNames('trend-icon-box', config.className)}>
                            <Icon icon={config.icon} width={16} height={16} />
                        </div>
                    )}
                    {renderInfo()}
                </div>
            </div>

            <div className='stat-card-value-row'>
                <span className='stat-card-main-value'>
                    {swapValuePercentage ? renderPercentage() : renderValue(value)}
                </span>
                {!hideComparison && (
                    <span className={classNames('stat-card-badge', config.className)}>
                        {swapValuePercentage ? renderValue(value) : renderPercentage()}
                    </span>
                )}
            </div>

            {!hideComparison && (
                <div className='stat-card-footer'>
                    <span className='stat-card-prev'>
                        {comparisonLabel ?? t('controls.statCard.previous', 'Vorige')}: {renderValue(prevValue)}
                    </span>
                    <span className={classNames('stat-card-comparison', config.className)}>
                        {diff === 0 ? '\u2014' : `(${diff < 0 ? '-' : '+'} ${renderValue(Math.abs(diff))})`}
                    </span>
                </div>
            )}
        </div>
    );
}
